import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import { ArrowRight, Globe2, ShieldCheck, Zap } from "lucide-react";
import Logo from "@/components/Logo";

const ez = mmEase.expo;

const STEPS = [
  {
    icon: Globe2,
    eyebrow: "Across continents",
    title: "Wherever family lives, we'll meet them there.",
    body: "Send between Africa, Europe, the UK, the US and Canada — bank, mobile money, or wallet to wallet.",
    color: "var(--color-success)",
  },
  {
    icon: Zap,
    eyebrow: "Made for speed",
    title: "Minutes, not days.",
    body: "Most transfers settle in under three minutes — track every step in real time.",
    color: "#06B6D4",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Built on trust",
    title: "Encryption that travels with you.",
    body: "Bank-grade security, biometric login, 2FA and a transaction PIN — your money, your control.",
    color: "#7C3AED",
  },
];

export default function Onboarding() {
  const [i, setI] = useState(0);
  const navigate  = useNavigate();
  const S = STEPS[i];
  const Icon = S.icon;

  return (
    <div className="min-h-screen hero-gradient grain relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(16,185,129,0.18) 0%, transparent 65%), " +
            "radial-gradient(ellipse 60% 50% at 90% 80%, rgba(6,182,212,0.10) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-3xl mx-auto px-6 py-8 min-h-screen flex flex-col">
        <Logo variant="dark" />

        <div className="flex-1 grid place-items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 28, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
              exit={  { opacity: 0, y: -20, filter: "blur(4px)" }}
              transition={{ duration: 0.55, ease: ez }}
              className="text-center max-w-xl"
            >
              <motion.div
                className="w-20 h-20 mx-auto rounded-2xl grid place-items-center mb-8"
                style={{ background: `${S.color}1A`, boxShadow: `0 8px 32px ${S.color}28` }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.45, ease: ez, delay: 0.06 }}
              >
                <Icon size={36} style={{ color: S.color }} />
              </motion.div>

              <p
                className="text-[10px] font-bold uppercase tracking-[0.22em] mb-4"
                style={{ color: S.color }}
              >
                {S.eyebrow}
              </p>
              <h2
                className="font-display font-black text-white leading-[1.08] text-balance"
                style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", letterSpacing: "-0.04em" }}
              >
                {S.title}
              </h2>
              <p className="text-white/60 text-[1rem] mt-5 leading-relaxed">{S.body}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between pb-4">
          {/* Dot indicators */}
          <div className="flex gap-2">
            {STEPS.map((_, idx) => (
              <button
                key={idx}
                data-testid={`onboarding-dot-${idx}`}
                onClick={() => setI(idx)}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: idx === i ? "40px" : "10px",
                  background: idx === i ? S.color : "rgba(255,255,255,0.28)",
                }}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="onboarding-skip"
              onClick={() => navigate("/register")}
              className="text-sm text-white/60 hover:text-white/90 transition-colors px-3 py-2"
            >
              Skip
            </button>
            <button
              data-testid="onboarding-next"
              onClick={() => (i < STEPS.length - 1 ? setI(i + 1) : navigate("/register"))}
              className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-200"
              style={{
                background: S.color,
                color: i === 0 ? "#0B1D3A" : "white",
                boxShadow: `0 4px 16px ${S.color}40`,
              }}
            >
              {i < STEPS.length - 1 ? "Next" : "Open my wallet"}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
